import * as THREE from "three";
import { getHatchTextureMaterialFromThreeMaterial } from "../../materials/designMaterialHatchTexture";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import type SSFXRendererScene from "./SSFXRendererScene";

const transparentMaterial = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: 0,
  name: "HatchMaterialPass transparentMaterial",
});

export class HatchMaterialPass extends EffectComposerPass {
  camera: THREE.Camera;
  scene: SSFXRendererScene | undefined;

  clear: boolean = false;

  private overrideMaterials = new WeakMap<THREE.Material, THREE.Material>();

  // Used in linework composition.
  private hatchRenderTarget: THREE.WebGLRenderTarget;
  private fsQuad: FullScreenQuad;
  private fsQuadMaterial: THREE.MeshBasicMaterial;

  constructor(scene: SSFXRendererScene | undefined, camera: THREE.Camera) {
    super();
    this.scene = scene;
    this.camera = camera;

    this.hatchRenderTarget = new THREE.WebGLRenderTarget(256, 256);
    this.fsQuadMaterial = new THREE.MeshBasicMaterial({
      map: this.hatchRenderTarget.texture,
      transparent: true,
      blending: THREE.NormalBlending,
      name: "HatchMaterialPass fsQuadMaterial",
    });
    this.fsQuad = new FullScreenQuad(this.fsQuadMaterial);
  }

  setSize(width: number, height: number): void {
    super.setSize(width, height);
    this.hatchRenderTarget.setSize(width, height);
  }

  updateScene(hatchThreshold: number): void {
    if (!this.scene) {
      return;
    }
    this.overrideMaterials = new WeakMap();
    this.scene.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        const materialOverride = obj.material.userData.materialParams
          ? getHatchTextureMaterialFromThreeMaterial(obj.material, hatchThreshold)
          : transparentMaterial;
        this.overrideMaterials.set(obj.material, materialOverride);
      }
    });
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null): void {
    if (!this.scene || !this.camera) {
      return;
    }

    const skybox = this.scene.sceneLightsManager.skybox;
    const oldSkyboxVisible = skybox.visible;

    // Disable the skybox to match PDF.
    skybox.visible = false;

    const oldClearColor = new THREE.Color("white");
    renderer.getClearColor(oldClearColor);
    renderer.setClearColor("white");
    const oldClearAlpha = renderer.getClearAlpha();
    renderer.setClearAlpha(0);

    this.scene.scene.overrideMaterial = this.overrideMaterials as any; // Using our three.js override materials patch

    // This clear prevents over-draw of the same hatching, giving excessively dark lines.
    renderer.setRenderTarget(this.hatchRenderTarget);
    renderer.clear(true, true, false);
    renderer.render(this.scene.scene, this.camera);

    skybox.visible = oldSkyboxVisible;
    this.scene.scene.overrideMaterial = null;

    renderer.setRenderTarget(writeBuffer);

    if (this.clear) {
      renderer.clear(true, true, false);
    }

    this.fsQuad.render(renderer);

    renderer.setClearColor(oldClearColor);
    renderer.setClearAlpha(oldClearAlpha);
  }

  dispose(): void {
    this.hatchRenderTarget.dispose();
  }
}
